import { useState, useMemo } from 'react';
import classnames from 'classnames';
import Image from 'components/atoms/Image';
import { useOrientation } from 'react-use';

import { useMedia, useWindowSize } from 'utils/hooks';

import styles from './index.css';

export interface ImageSliderProp {
    className?: string;
    images: string[];
}

const ImageSlider: React.FC<ImageSliderProp> = ({ className, images = [] }) => {
    const [activeIndex, updateActiveIndex] = useState(0);
    const { width: windowWidth, height: windowHeight } = useWindowSize();
    const { media, mediaScale } = useMedia();
    const { type } = useOrientation();

    const imageWidth = useMemo(() => {
        let padding = 77;

        if (mediaScale !== 1) {
            padding = 77 * mediaScale;
        }

        if (media === 'phone') {
            if (windowWidth > windowHeight) {
                padding = 40;
            } else {
                padding = 35;
            }
        }

        return windowWidth - padding * 2;
    }, [media, mediaScale, type, windowWidth]);

    const imageHeight = useMemo(() => {
        let padding = 77;

        if (mediaScale !== 1) {
            padding = 77 * mediaScale;
        }

        if (media === 'phone') {
            if (windowWidth > windowHeight) {
                padding = 40;
            } else {
                padding = 148;
            }
        }

        return windowHeight - padding * 2;
    }, [media, mediaScale, type, windowWidth, windowHeight]);

    return (
        <div className={classnames(styles.imageSlider, className)}>
            <div className={styles.imageWrapper}>
                {images.map((src, index) => (
                    <Image
                        wrapperClassName={classnames(
                            styles.image,
                            index === activeIndex && styles.active,
                        )}
                        key={src}
                        src={src}
                        width={imageWidth}
                        height={imageHeight}
                        reset={index !== activeIndex}
                    />
                ))}
            </div>
            {images.length > 1 && (
                <div className={styles.pagination}>
                    {images.map((src, index) => (
                        <button
                            key={src}
                            className={classnames(
                                styles.page,
                                index === activeIndex && styles.active,
                            )}
                            onClick={() => updateActiveIndex(index)}
                            type="button"
                            aria-label={`page ${index}`}
                        >
                            <span className={styles.icon} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageSlider;
